import { FormikValues, useFormik } from 'formik';
import { forEach, get, isEmpty, isObject } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { fillEmptyTranslations } from './../common/utils/format-utils';
import { dateFormat } from './useDate';

type OneThunkProps = (id: string | number) => (dispatch: Dispatch<any>) => Promise<any>;
type WarehouseThunkProps = () => (dispatch: Dispatch<any>) => Promise<any>;
type ClearThunkProps = () => (dispatch: Dispatch<any>) => Promise<any>;
type CreateThunkProps = (values: FormikValues) => (dispatch: Dispatch<any>) => Promise<any>;
type EditThunkProps = (
  id: string | number,
  values: FormikValues
) => (dispatch: Dispatch<any>) => Promise<any>;

interface UseEditProps {
  id?: string | number;
  entity: string;
  initialValues: Record<string, any>;
  validationSchema: any;
  onGetWareHouse?: WarehouseThunkProps;
  onGetOne: OneThunkProps;
  onClearOne: ClearThunkProps;
  onClear: ClearThunkProps;
  onCreate: CreateThunkProps;
  onEdit: EditThunkProps;
  onSubmit: (res: any) => void;
}

export const useEdit = ({
  id,
  entity,
  initialValues,
  validationSchema,
  onGetWareHouse,
  onGetOne,
  onClearOne,
  onClear,
  onCreate,
  onEdit,
  onSubmit,
}: UseEditProps) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { request, one } = useSelector((state: RootStateOrAny) => state[entity]);

  const fillTranslates = (values: Record<string, any>) => {
    const result = { ...values };
    forEach(result, (value: any, key) => {
      if (isObject(value) && 'ru' in value && 'uz' in value && 'oz' in value) {
        result[key] = fillEmptyTranslations(value, i18n.language);
      }
    });
    return result;
  };

  const handleSubmit = async (formikValues: FormikValues) => {
    const values = await fillTranslates(formikValues);

    dispatch(onClear());
    if (!!id) {
      onEdit(id, values)(dispatch).then(handleAfterSubmit);
    } else {
      onCreate(values)(dispatch).then(handleAfterSubmit);
    }
  };

  const handleAfterSubmit = (res: any) => {
    if (onGetWareHouse) dispatch(onGetWareHouse());
    onSubmit(res);
  };

  const handleFieldChange = (field: string, e: any) => {
    formik.setFieldValue(field, e.target.value);
    formik.setFieldError(field, null);
  };

  const handleSelectChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, null);
  };

  const handleDateProps = (field: string) => {
    let value = get(formik, ['values', field], '');
    if (value) {
      const date: any = new Date(value);
      if (date !== 'Invalid Date') value = date;
    }
    return {
      ...formik.getFieldProps(field),
      value,
      onChange: (date: any) => {
        if (date !== 'Invalid Date') {
          formik.setFieldValue(field, new Date(date).toISOString());
        }
        formik.setFieldError(field, null);
      },
    };
  };

  const handleTimeProps = (field: string) => {
    let value = get(formik, ['values', field], '');
    if (value) {
      const date: any = new Date('2020-12-12 ' + value);
      if (date !== 'Invalid Date') value = date;
    }
    return {
      ...formik.getFieldProps(field),
      value,
      onChange: (time: any) => {
        if (time !== 'Invalid Date') {
          formik.setFieldValue(field, dateFormat(new Date(time), 'T'));
        }
        formik.setFieldError(field, null);
      },
    };
  };

  const hasError = (field: string) => {
    return (
      get(formik, ['touched', ...field.split('.')], false) &&
      Boolean(get(formik, ['errors', ...field.split('.')], false))
    );
  };

  const getErrorMessage = (field: string) => {
    if (!hasError(field)) return ' ';
    const key = get(formik, ['errors', ...field.split('.')], 'error');
    if (key.includes('error.')) {
      return t(key, 'Ошибка');
    }
    return key;
  };

  const formik: any = useFormik({
    initialValues,
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (!!id) dispatch(onGetOne(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isObject(request?.error)) {
      forEach(request?.error, (value, field) => formik.setFieldError(field, value[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.error]);

  useEffect(() => {
    if (!isEmpty(one?.data)) {
      forEach(one?.data, (value, field) => formik.setFieldValue(field, value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [one?.data]);

  useEffect(() => {
    return () => {
      dispatch(onClear());
      dispatch(onClearOne());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...request,
    isFetching: one.isLoading,
    formik,
    handleFieldChange,
    handleSelectChange,
    handleDateProps,
    handleTimeProps,
    hasError,
    getErrorMessage,
  };
};
